import React, { useEffect, useRef } from 'react';
import { Alert, Stack, TextField, Typography } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import moment from 'moment';

const ModalFechasCurso = ({ diasTotalesPeriodo }) => {
    const { control, watch, setValue, getValues } = useFormContext();

    const fechaInicio = watch('fechaInicioCurso');
    const fechaFin = watch('fechaFinCurso');

    // Bandera para saber si el usuario ya edito manualmente la fecha fin
    const fechaFinEditadaManualmente = useRef(false);

    const getFechaHoy = () => {
        const hoy = new Date();
        const year = hoy.getFullYear();
        const month = String(hoy.getMonth() + 1).padStart(2, '0');
        const day = String(hoy.getDate()).padStart(2, '0');
        const hours = String(hoy.getHours()).padStart(2, '0');
        const minutes = String(hoy.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const getFechaMinFin = () => {
        if (!fechaInicio) return getFechaHoy();
        const fecha = new Date(fechaInicio);
        fecha.setDate(fecha.getDate() + 1);
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        const hours = String(fecha.getHours()).padStart(2, '0');
        const minutes = String(fecha.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const calcularFechaFinSugerida = (fechaInicioValor) => {
        if (!fechaInicioValor || !diasTotalesPeriodo || diasTotalesPeriodo <= 0) return null;

        const fin = moment(fechaInicioValor).add(diasTotalesPeriodo, 'days');
        return fin.format('YYYY-MM-DDTHH:mm');
    };

    useEffect(() => {
        if (!fechaInicio) {
            const hoy = getFechaHoy();
            setValue('fechaInicioCurso', hoy);

            if (diasTotalesPeriodo > 0) {
                const finSugerido = calcularFechaFinSugerida(hoy);
                if (finSugerido) setValue('fechaFinCurso', finSugerido);
            }
        }
    }, []);

    useEffect(() => {
        if (fechaInicio && diasTotalesPeriodo > 0 && !fechaFinEditadaManualmente.current) {
            const finSugerido = calcularFechaFinSugerida(fechaInicio);
            if (finSugerido) setValue('fechaFinCurso', finSugerido);
        }
    }, [fechaInicio]);

    const calcularDuracion = () => {
        if (!fechaInicio || !fechaFin) return null;

        const inicio = moment(fechaInicio);
        const fin = moment(fechaFin);

        if (!inicio.isValid() || !fin.isValid() || fin.isSameOrBefore(inicio)) return null;

        const duracionTotal = moment.duration(fin.diff(inicio));

        const dias = Math.floor(duracionTotal.asDays());
        const horas = duracionTotal.hours();
        const minutos = duracionTotal.minutes();

        return { dias, horas, minutos };
    };

    const duracion = calcularDuracion();

    const formatearDuracion = (duracion) => {
        if (!duracion) return '';
        const { dias, horas, minutos } = duracion;

        const partes = [];
        if (dias > 0) partes.push(`${dias} día${dias !== 1 ? 's' : ''}`);
        if (horas > 0) partes.push(`${horas} hora${horas !== 1 ? 's' : ''}`);
        if (minutos > 0 && dias === 0) partes.push(`${minutos} minuto${minutos !== 1 ? 's' : ''}`);

        if (partes.length === 0) return 'menos de un minuto';

        return partes.join(' y ');
    };

    return (
        <Stack spacing={3} sx={{ mt: 2, mb: 2 }}>
            {diasTotalesPeriodo > 0 && (
                <Alert severity="info" variant="outlined">
                    El curso cuenta con <strong>{diasTotalesPeriodo} día(s)</strong> de periodo configurados en los materiales de las
                    actividades.
                </Alert>
            )}

            <Controller
                control={control}
                name="fechaInicioCurso"
                defaultValue={getFechaHoy()}
                rules={{
                    required: 'La fecha de inicio es requerida'
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                        label="Fecha inicio del curso"
                        type="datetime-local"
                        value={value || getFechaHoy()}
                        onChange={(e) => {
                            onChange(e);
                        }}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        required
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            min: getFechaHoy()
                        }}
                    />
                )}
            />
            <Controller
                control={control}
                name="fechaFinCurso"
                defaultValue=""
                rules={{
                    required: 'La fecha de fin es requerida',
                    validate: (value) => {
                        if (!value) return 'La fecha de fin es requerida';
                        if (fechaInicio && new Date(value) <= new Date(fechaInicio)) {
                            return 'La fecha de fin debe ser mayor a la fecha de inicio';
                        }
                        return true;
                    }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                        label="Fecha fin del curso"
                        type="datetime-local"
                        value={value || ''}
                        onChange={(e) => {
                            fechaFinEditadaManualmente.current = true;
                            onChange(e);
                        }}
                        error={!!error}
                        //helperText={error?.message}
                        fullWidth
                        required
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            min: getFechaMinFin()
                        }}
                    />
                )}
            />

            {duracion && (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                    El curso contará con una duración de {formatearDuracion(duracion)}.
                </Typography>
            )}
        </Stack>
    );
};

export default ModalFechasCurso;
